import {
  type InlineModel,
  beIntegrationCatalogGetIntegrationsByTag,
} from 'Api/beIntegrationCatalogApi'

import { beFattureinCloudApiGetListConfiguration } from 'Api/beFattureInCloudApi'

import {
  STORE_KEY_ACCOUNT,
  STORE_KEY_ID_LIST,
  evalCondition,
  getLang,
  notifyParent,
  storeGet,
} from 'mailup-react-helpers'

import getIntl from 'Helpers/intl'

import importApp from 'Icons/import/import_app.svg'
import importEmail from 'Icons/import/import_email.svg'
import importExcel from 'Icons/import/import_excel.svg'
import importSms from 'Icons/import/import_sms.svg'
import importTxt from 'Icons/import/import_txt.svg'
import importXml from 'Icons/import/import_xml.svg'

import { FATTURE_IN_CLOUD_INTEGRATION_ID } from 'Constants'
import type { IntegrationsListState } from 'Pages/ImportLanding/model'

import { goToFattureInCloudConnector } from 'Helpers/settings'

export const sortIntegrations = (a: InlineModel, b: InlineModel): number => {
  if (a.client_id === FATTURE_IN_CLOUD_INTEGRATION_ID) return -1
  if (b.client_id === FATTURE_IN_CLOUD_INTEGRATION_ID) return 1
  return 0
}

export const getIntegrationsAction =
  async (): Promise<IntegrationsListState> => {
    const whiteLabel: boolean = evalCondition('co:WHITELABEL')

    const idList: number = storeGet(STORE_KEY_ID_LIST)

    if (whiteLabel) {
      return {
        items: [],
        totalItems: 0,
        whiteLabel,
        ficStatus: undefined,
      }
    }

    const intl = getIntl()

    const lang: string = getLang()
    const { Domain }: { Domain: string } = storeGet(STORE_KEY_ACCOUNT)

    const [result, ficListConfig] = await Promise.all([
      await beIntegrationCatalogGetIntegrationsByTag(lang, 'import'),
      beFattureinCloudApiGetListConfiguration(idList),
    ])

    const integrationSiteLang = ['it', 'es'].includes(lang) ? lang : 'en'

    return {
      items: [
        ...result
          .sort(sortIntegrations)
          .map(({ title, logo, excerpt, url, client_id }) => ({
            url:
              client_id === FATTURE_IN_CLOUD_INTEGRATION_ID ? undefined : url,
            cb:
              client_id === FATTURE_IN_CLOUD_INTEGRATION_ID
                ? goToFattureInCloudConnector
                : undefined,
            title,
            excerpt,
            logo:
              logo !== null && logo !== undefined && logo.length > 0
                ? `https://${Domain}/appImages/${logo}`
                : undefined,
          })),
        {
          url: `https://integrations.mailup.com/${integrationSiteLang}`,
          title: intl.formatMessage({
            id: 'Load.ListBuildingTools.lblListBuildingOther',
          }),
          excerpt: intl.formatMessage({
            id: 'Load.ListBuildingTools.lblListBuildingOtherSubtitle',
          }),
          logo: importApp,

          className: 'test-integrationDoc',
          dataAttributeTest: 'integrationDoc',
        },
      ],
      totalItems: result.length + 1,
      whiteLabel,
      ficStatus: ficListConfig?.Status,
    }
  }

interface ImportOption {
  onClick: () => void
  text: string
  img: string
  cta: string
  className: string
  dataAttributeTest: string
}

export const getOptionsAction = (): ImportOption[] => {
  const noImport: boolean = evalCondition('uo:NOIMPORT')
  const intl = getIntl()

  return !noImport
    ? [
        {
          onClick: () => {
            notifyParent('redirectMainFrame', ['/console/form_import.aspx'])
          },
          text: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblImportCsvDesc',
          }),
          img: importTxt,
          cta: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblImportCsv',
          }),
          className: 'test-importCsv',
          dataAttributeTest: 'importCsv',
        },
        {
          onClick: () => {
            notifyParent('redirectMainFrame', ['/console/form_import_xls.aspx'])
          },
          text: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblImportXlsDesc',
          }),
          img: importExcel,
          cta: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblImportXls',
          }),
          className: 'test-importXls',
          dataAttributeTest: 'importXls',
        },
        {
          onClick: () => {
            notifyParent('redirectMainFrame', ['/console/form_import_xml.aspx'])
          },
          text: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblImportXmlDesc',
          }),
          img: importXml,
          cta: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblImportXml',
          }),
          className: 'test-importXml',
          dataAttributeTest: 'importXml',
        },
        {
          onClick: () => {
            notifyParent('redirectMainFrame', [
              '/console/form_import_extractor.aspx',
            ])
          },
          text: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblCopyPasteEmailDesc',
          }),
          img: importEmail,
          cta: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblCopyPasteEmail',
          }),
          className: 'test-copyPasteEmail',
          dataAttributeTest: 'copyPasteEmail',
        },
        {
          onClick: () => {
            notifyParent('redirectMainFrame', [
              '/console/form_import_extractor_SMS.aspx',
            ])
          },
          text: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblCopyPasteSmsDesc',
          }),
          img: importSms,
          cta: intl.formatMessage({
            id: 'Load.ImportLandingPage.lblCopyPasteSms',
          }),
          className: 'test-copyPasteSms',
          dataAttributeTest: 'copyPasteSms',
        },
      ]
    : []
}
